import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Clock } from "lucide-react";

export default function AdminAttendance() {
  const [batches, setBatches] = useState([]);
  const [wards, setWards] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [attendance, setAttendance] = useState({});

  useEffect(() => {
    api.get("/admin/batches").then((r) => {
      setBatches(r.data);
      if (r.data.length && !batchId) setBatchId(r.data[0].id);
    });
    api.get("/parent/wards").then((r) => setWards(r.data));
  }, []);

  useEffect(() => {
    if (!batchId) return;
    api.get("/admin/attendance", { params: { batch_id: batchId, date } }).then((r) => {
      const m = {};
      r.data.forEach((a) => { m[a.ward_id] = a.status; });
      setAttendance(m);
    });
  }, [batchId, date]);

  const mark = async (wardId, status) => {
    try {
      await api.post("/admin/attendance", { ward_id: wardId, batch_id: batchId, date, status });
      setAttendance({ ...attendance, [wardId]: status });
      toast.success("Marked");
    } catch (e) { toast.error(apiError(e)); }
  };

  const currentBatch = batches.find((b) => b.id === batchId);
  // wards eligible: sport matches
  const eligible = currentBatch ? wards.filter((w) => w.sport === currentBatch.sport) : [];

  return (
    <div data-testid="admin-attendance">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Academy · Attendance</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-8">Attendance.</h1>

      <div className="flex flex-wrap gap-3 mb-8">
        <select value={batchId} onChange={(e) => setBatchId(e.target.value)} data-testid="attendance-batch" className="bg-[#131A2B] border border-white/12 py-2 px-4">
          {batches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date" className="bg-[#131A2B] border border-white/12 py-2 px-4" />
      </div>

      <div className="border border-white/12 bg-[#131A2B]">
        <table className="w-full">
          <thead>
            <tr className="font-mono text-xs uppercase tracking-widest text-white/40 border-b border-white/12">
              <th className="text-left p-4">Ward</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Mark</th>
            </tr>
          </thead>
          <tbody>
            {eligible.map((w) => (
              <tr key={w.id} className="border-b border-white/5" data-testid={`attendance-row-${w.id}`}>
                <td className="p-4">
                  <div className="font-bold">{w.name}</div>
                  <div className="text-xs text-white/50 font-mono">Age {w.age}</div>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 text-xs font-mono uppercase ${
                    attendance[w.id] === "present" ? "bg-brand-blue/20 text-brand-blue" :
                    attendance[w.id] === "absent" ? "bg-red-500/20 text-red-400" :
                    attendance[w.id] === "late" ? "bg-yellow-500/20 text-yellow-400" :
                    "text-white/30"
                  }`}>{attendance[w.id] || "unmarked"}</span>
                </td>
                <td className="p-4 text-right space-x-1">
                  <button onClick={() => mark(w.id, "present")} data-testid={`mark-present-${w.id}`} className="p-2 border border-white/20 hover:border-brand-blue hover:text-brand-blue"><Check size={12} /></button>
                  <button onClick={() => mark(w.id, "late")} data-testid={`mark-late-${w.id}`} className="p-2 border border-white/20 hover:border-yellow-400 hover:text-yellow-400"><Clock size={12} /></button>
                  <button onClick={() => mark(w.id, "absent")} data-testid={`mark-absent-${w.id}`} className="p-2 border border-white/20 hover:border-red-400 hover:text-red-400"><X size={12} /></button>
                </td>
              </tr>
            ))}
            {eligible.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-white/40">No wards match this batch's sport.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
